import { useNavigate } from "react-router-dom"
import { DataTypeForm, Labels } from "../../../../../features/data-types"
import { Async, Data, FieldType, FieldValue, FileField, FileType, FormField, ModalId, O, option, PanelWrapper, useEffectAfterMount, useGetCrumbs, useUIActions } from "../../../../../features/ui"
import { PurchaseOrder } from "../../../../purchases"
import { useActions } from "./actions"
import { useEffect, useMemo, useState } from "react"
import { TableName } from '../../../../../tables'
import { TaxType, Ticket, TicketType } from '../../../state/types'
import { TaxItemsTable } from '../../tax/tax-items-table/taxItemsTable'
import { TaxItemFormModal } from '../../tax/tax-item-form-modal/taxItemFormModal'
import { fileHelpers, useFileActions } from '../../../../../features/files'
import { useTaxLoaders } from '../../../loaders/taxLoaders'
import { TicketAmountDiffersModal } from '../ticket-amount-differs-modal/ticketAmountDiffersModal'
import { operationHelpers, usePriceOutlineHooks } from '../../../../operations'
import { RoutePath, useRoutesHooks } from '../../../../../features/routes'

export const TicketForm = () => {
  const { dataType, secondDataType } = useRoutesHooks().parseUrl()
  const purchase = dataType! as PurchaseOrder
  const ticket = secondDataType as O<Ticket>
  const createMode = !ticket

  const [type, setType] = useState<O<TicketType>>(ticket?.type || TicketType.TICKET_A)
  const [ticketFile, setTicketFile] = useState<O<File>>()
  const [dueDate, setDueDate] = useState<O<Date>>(ticket?.dueDate ? new Date(ticket.dueDate) : undefined)
  const [subtotal, setSubtotal] = useState<O<number>>(ticket?.subtotal)
  const [vatAmount, setVatAmount] = useState<O<number>>(ticket?.vatAmount)
  const [taxItemsData, setTaxItemsData] = useState<Data[]>(ticket?.taxItems || [])
  const [currentTaxItem, setCurrentTaxItem] = useState<Data>()
  const [total, setTotal] = useState<O<number>>(ticket?.total)
  const [modalData, setModalData] = useState<O<Data>>()
  const [loaded, setLoaded] = useState<boolean>(false)

  const { describeCode } = operationHelpers
  const { purchaseTicketFileName } = fileHelpers
  const { getPriceOutline } = usePriceOutlineHooks()
  const navigate = useNavigate()

  const taxLoaders = useTaxLoaders()

  const { submitTicketOrOpenModal, submitTicket } = useActions()
  const { openModal, closeModal } = useUIActions()
  const { fetchFile } = useFileActions()

  const priceOutline = useMemo(() => getPriceOutline(TableName.PURCHASE_ORDERS, purchase), [purchase])

  const load = async () => {
    if (createMode) {
      setSubtotal(priceOutline.totalWithoutVAT)
      setVatAmount(priceOutline.vatAmount)
    } else if (ticket.ticketUrl) {
      const file = await fetchFile(ticket.ticketUrl!)
      setTicketFile(file)
    }
  }

  useEffect(() => {
    load().then(() => setLoaded(true))
  }, [])

  useEffectAfterMount(() => {
    subtotal && setTotal(subtotal + (vatAmount || 0) + taxItemsData.reduce((sum, taxItemData) => sum + (taxItemData.amount! as number), 0))
  }, [subtotal, vatAmount, taxItemsData])

  const ticketLabel = ticket ? `: ${describeCode(ticket) }` : ''
  const title = `${createMode ? 'Subir' : 'Editar'} Factura${ticketLabel}`
  const crumbs = useGetCrumbs(
    'Compras',
    RoutePath.PURCHASES,
    describeCode(purchase),
    purchase.id,
    title
  )

  const perceptions = useMemo(() => taxLoaders().findAllTaxes().filter(tax =>
    tax.type === TaxType.PERCEPTION && (
      !tax.archived && !taxItemsData.map(taxItemData => taxItemData.taxCode).includes(tax.code)
    )
  ), [taxItemsData, currentTaxItem])

  const noIVATypes = useMemo(() => [TicketType.TICKET_C, TicketType.RECEIPT_X], [])
  const noPerceptionTypes = useMemo(() => [TicketType.TICKET_B, TicketType.TICKET_C, TicketType.RECEIPT_X], [])

  const taxItemMatches = (data: Data, taxCode: string) => data.taxCode === taxCode
  const openFormModal = () => openModal(ModalId.TAX_ITEM_FORM)

  const onChangeType = (value?: FieldValue) => {
    const newType = value as O<TicketType>
    setType(newType)
    if (newType && noIVATypes.includes(newType)) {
      setTaxItemsData([])
      setSubtotal(priceOutline.total)
      setVatAmount(0)
    } else {
      if (newType && noPerceptionTypes.includes(newType)) setTaxItemsData([])
      setSubtotal(priceOutline.totalWithoutVAT)
      setVatAmount(priceOutline.vatAmount)
    }
  }

  const onChangeSubtotal = (value?: FieldValue) => {
    setSubtotal(value as O<number>)
  }

  const onChangeVatAmount = (value?: FieldValue) => {
    setVatAmount(value as O<number>)
  }

  const onChangeDueDate = (value?: FieldValue) => {
    setDueDate(value as O<Date>)
  }

  const onAddTaxItem = () => {
    setCurrentTaxItem(undefined)
    openFormModal()
  }

  const onEditTaxItem = (taxCode: string) => {
    setCurrentTaxItem(taxItemsData.find(taxItemData => taxItemMatches(taxItemData, taxCode)))
    openFormModal()
  }

  const onRemoveTaxItem = (taxCode: string) => {
    const newTaxItemsData = taxItemsData.filter(taxItemData => !taxItemMatches(taxItemData, taxCode))
    setTaxItemsData(newTaxItemsData)
  }

  const onChangeTotal = (value?: FieldValue) => {
    const newTotal = value as O<number>
    setTotal(newTotal)
    if (type && noIVATypes.includes(type)) {
      subtotal !== newTotal && setSubtotal(newTotal)
    }
  }

  const fields: FormField[] = [
    {
      name: 'ticketUrl',
      type: FieldType.FILE,
      label: 'Factura',
      hint: 'Formato: PDF, JPG, PNG',
      loading: !loaded,
      render: () => <Async
        loadFn={async () => (
          <FileField
            id="ticketUrl"
            value={ticketFile}
            fileName={purchaseTicketFileName()}
            fileTypes={[FileType.PDF, FileType.JPEG, FileType.PNG]}
            onChange={setTicketFile}
          />
        )}
        deps={[ticketFile]}
      />
    },
    {
      name: 'type',
      type: FieldType.SELECT,
      label: 'Tipo de Documento',
      value: type,
      options: Object.values(TicketType)
        .filter(type => type !== TicketType.SALARY)
        .map(type => option(type, Labels.ticketTypeLabel(type))),
      onChange: onChangeType
    },
    {
      name: 'dueDate',
      type: FieldType.DATE,
      label: 'Fecha de Vto.',
      value: dueDate,
      min: new Date(),
      onChange: onChangeDueDate
    },
    {
      name: 'total',
      type: FieldType.PRICE,
      label: 'Monto Final',
      value: total,
      loading: !loaded,
      onChange: onChangeTotal
    }
  ]
  if (!type || !noIVATypes.includes(type)) {
    const subtotalField = {
      name: 'subtotal',
      type: FieldType.PRICE,
      label: 'Subtotal Productos',
      value: subtotal,
      loading: !loaded,
      onChange: onChangeSubtotal
    }
    const vatAmountField = {
      name: 'vatAmount',
      type: FieldType.PRICE,
      label: 'IVA',
      value: vatAmount,
      loading: !loaded,
      onChange: onChangeVatAmount
    }
    const fieldsToInsert: FormField[] = [subtotalField, vatAmountField]
    if (!type || !noPerceptionTypes.includes(type)) {
      fieldsToInsert.push({
        name: 'taxItems',
        type: FieldType.TABLE,
        label: 'Percepciones',
        render: () => (
          <TaxItemsTable
            taxItems={taxItemsData}
            disableCreate={!subtotal || perceptions.length === taxItemsData.length}
            onCreate={onAddTaxItem}
            onEditRow={onEditTaxItem}
            onRemoveRow={onRemoveTaxItem}
          />
        )
      })
    }
    fields.splice(3, 0, ...fieldsToInsert)
  }

  const onSubmit = (ticketData: Data) => {
    setModalData(ticketData)
    submitTicketOrOpenModal(
      ticketData,
      priceOutline,
      purchase.id,
      subtotal!,
      vatAmount!,
      taxItemsData,
      createMode,
      ticketFile
    )
  }

  const onCancel = () => navigate(-1)

  const onSaveTaxItem = (newData: Data) => {
    let newTaxItemsData = taxItemsData
    if (currentTaxItem) {
      const { taxCode } = currentTaxItem
      newTaxItemsData = taxItemsData.map(taxItemData => taxItemMatches(taxItemData, taxCode! as string) ? newData : taxItemData)
    } else {
      newTaxItemsData = [newData, ...taxItemsData]
    }
    setTaxItemsData(newTaxItemsData)
  }

  const onSubmitModal = (data: Data) => {
    closeModal(ModalId.TICKET_AMOUNT_DIFFERS)
    submitTicket(
      data,
      purchase.id,
      taxItemsData,
      createMode,
      ticketFile
    )
  }

  return (
    <PanelWrapper
      title={title}
      crumbs={crumbs}
    >
      <DataTypeForm
        formId="ticket-form"
        fields={fields}
        createMode={createMode}
        createLabel="Guardar"
        onSubmit={onSubmit}
        onCancel={onCancel}
      />
      <TaxItemFormModal
        taxItem={currentTaxItem}
        total={subtotal}
        title="Agregar Percepción"
        editableAmount
        taxes={perceptions}
        onSubmit={onSaveTaxItem}
      />
      <TicketAmountDiffersModal
        data={modalData}
        purchase={purchase}
        onSubmit={onSubmitModal}
      />
    </PanelWrapper>
  )
}
